import {
  createStableObjectHash,
  getFixedOptions,
  hasAltLabel,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const valueKey = (val: unknown): string =>
  typeof val === 'object' && val !== null ? createStableObjectHash(val as object) : String(val);

export type GroupedOptionList<T> = {
  selected: Array<SelectItem<T>>;
  notSelected: Array<SelectItem<T>>;
};

type UseMultiSelectOptionsParams<T> = {
  options?: SelectItem<T>[];
  unfilteredOptions?: SelectItem<T>[];
  fixedOptions: SelectItem<T>[];
  value: T[];
  memoizedValueList: T[];
  searchInput: string | undefined;
  customSelectItemFn?: (value: T, options: SelectItem<T>[]) => SelectItem<T> | undefined;
};

export function useMultiSelectOptions<T>({
  options,
  unfilteredOptions,
  fixedOptions,
  value,
  memoizedValueList,
  searchInput,
  customSelectItemFn,
}: UseMultiSelectOptionsParams<T>) {
  const { t } = useTranslation();

  const searchResults = useMemo(() => {
    if (!searchInput) return fixedOptions;
    const lowerSearch = searchInput.toLowerCase();
    return fixedOptions.filter(({ label: optionLabel, category: optionCategory }) => {
      const labelText = String(optionLabel).toLowerCase();
      const categoryText = String(optionCategory || '').toLowerCase();
      return labelText.includes(lowerSearch) || categoryText.includes(lowerSearch);
    });
  }, [fixedOptions, searchInput]);

  const selectedValueSet = useMemo(() => new Set(value.map(valueKey)), [value]);

  const isOptionSelected = useCallback(
    (option: SelectItem<T>) => selectedValueSet.has(valueKey(option.value)),
    [selectedValueSet],
  );

  const memoizedValueSet = useMemo(() => new Set(memoizedValueList.map(valueKey)), [memoizedValueList]);

  const groupedOptionsBySelectedStatus = useMemo(() => {
    return searchResults.reduce<GroupedOptionList<T>>(
      (acc, currentOption) => {
        if (memoizedValueSet.has(valueKey(currentOption.value))) acc.selected.push(currentOption);
        else acc.notSelected.push(currentOption);
        return acc;
      },
      { selected: [], notSelected: [] },
    );
  }, [searchResults, memoizedValueSet]);

  const sortOptionsByCategory = useCallback(
    (groupedOptions: Array<SelectItem<T>>) => {
      if (!groupedOptions.some(opt => opt.category != null)) return groupedOptions;
      return [...groupedOptions].sort((a, b) => {
        const orderDiff = (a.categoryOrder ?? 0) - (b.categoryOrder ?? 0);
        if (orderDiff !== 0) return orderDiff;
        const categoryA = a.category?.toLowerCase() ?? t('components.dropdownPanel.noCategory');
        const categoryB = b.category?.toLowerCase() ?? t('components.dropdownPanel.noCategory');
        return categoryA.localeCompare(categoryB);
      });
    },
    [t],
  );

  const notSelectedOptions = useMemo(() => {
    const sortedOptionsByCategory = sortOptionsByCategory(groupedOptionsBySelectedStatus.notSelected);
    const seen = new Set<string>();
    return sortedOptionsByCategory.filter(option => {
      const dedupKey = `${valueKey(option.value)}::${option.category ?? ''}`;
      if (seen.has(dedupKey)) return false;
      seen.add(dedupKey);
      return true;
    });
  }, [groupedOptionsBySelectedStatus.notSelected, sortOptionsByCategory]);

  const selectedOptions = useMemo(() => {
    const seen = new Set<string>();
    const deduped = groupedOptionsBySelectedStatus.selected.filter(option => {
      const dedupKey = `${valueKey(option.value)}::${option.category ?? ''}`;
      if (seen.has(dedupKey)) return false;
      seen.add(dedupKey);
      return true;
    });
    // Cascading filters: keep still-applicable selections first, push the
    // orphaned (disabled) ones to the bottom of the selected group.
    return deduped.sort((optionA, optionB) => Number(optionA.disabled ?? false) - Number(optionB.disabled ?? false));
  }, [groupedOptionsBySelectedStatus.selected]);

  const categoriesToDisplay = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ category: string; index: number }> = [];
    notSelectedOptions.forEach((cur, index) => {
      const category = cur.category || 'undefined';
      if (!seen.has(category)) {
        seen.add(category);
        result.push({ category, index });
      }
    });
    return result;
  }, [notSelectedOptions]);

  const dedupedSelectedOptions = useMemo(() => {
    return Array.from(new Map(selectedOptions.map(opt => [valueKey(opt.value), opt])).values());
  }, [selectedOptions]);

  const selectedOptionValues = useMemo(() => dedupedSelectedOptions.map(opt => opt.value), [dedupedSelectedOptions]);
  const notSelectedOptionValues = useMemo(() => notSelectedOptions.map(opt => opt.value), [notSelectedOptions]);

  const hasCategorizedOptions = useMemo(
    () => notSelectedOptions.some(opt => opt.category != null),
    [notSelectedOptions],
  );

  const needsDivider =
    !!groupedOptionsBySelectedStatus.selected.length && !!groupedOptionsBySelectedStatus.notSelected.length;

  const dropdownButtonFixedOptions = useMemo(
    () => getFixedOptions(options, unfilteredOptions),
    [options, unfilteredOptions],
  );

  const dropdownButtonValue = useMemo(() => {
    if (value.length === 0) return '';

    const optionsByValueKey = new Map<string, SelectItem<T>>();
    for (const opt of dropdownButtonFixedOptions) {
      const k = valueKey(opt.value);
      if (!optionsByValueKey.has(k)) {
        optionsByValueKey.set(k, opt);
      }
    }

    const buttonValue = value
      .map(currentValue => {
        if (customSelectItemFn && currentValue) {
          return customSelectItemFn(currentValue, dropdownButtonFixedOptions);
        }
        return optionsByValueKey.get(valueKey(currentValue));
      })
      .filter((selectItem): selectItem is SelectItem<T> => selectItem != null);
    return buttonValue
      .sort((optA, optB) => {
        const optALabel = hasAltLabel(optA) ? optA.altLabel : optA.label;
        const optBLabel = hasAltLabel(optB) ? optB.altLabel : optB.label;
        return optALabel.localeCompare(optBLabel, undefined, { sensitivity: 'base' });
      })
      .map(option => (hasAltLabel(option) ? option.altLabel : option.label))
      .join(', ');
  }, [dropdownButtonFixedOptions, value, customSelectItemFn]);

  return {
    searchResults,
    isOptionSelected,
    notSelectedOptions,
    dedupedSelectedOptions,
    selectedOptionValues,
    notSelectedOptionValues,
    categoriesToDisplay,
    hasCategorizedOptions,
    needsDivider,
    dropdownButtonValue,
  };
}
